/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	OnInit,
	AfterViewInit,
	OnDestroy,
	Input,
	ElementRef,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	HostListener,
} from '@angular/core';
import { Subject } from 'rxjs';
import * as Hammer from 'hammerjs';
import { ContainerLayerManager, ContainerType } from './ContainerLayerManager';

@Component({
	template: '',
	changeDetection: ChangeDetectionStrategy.OnPush,
})

/**
 * Abstraction of some container behavior and user interactions based updates
 * This base container uses OnPush change detection
 * updatePosition() limits the container within viewport and invokes changeDetectorRef.detectChanges()
 * updatePosition() should be invoked whenever container dimension/position need to be updated
 */
export class BaseContainerComponent implements OnInit, AfterViewInit, OnDestroy {
	@Input() public width: number;
	@Input() public height: number;
	@Input() public x: number;
	@Input() public y: number;
	@Input() public minWidth: number;
	@Input() public minHeight: number;
	@Input() public deadZoneSize: number;
	@Input() public zIndex: number; // designated z-index
	@Input() public containerType: ContainerType;

	public container: ElementRef;
	public currentZIndex: number;

	protected shutdown$ = new Subject<void>();
	protected isInitialized: boolean;
	protected viewportWidth: number;
	protected viewportHeight: number;
	protected borderTopBottom: number;
	protected borderLeftRight: number;

	private windowResizeHandler: () => void;

	constructor(
		protected changeDetectorRef: ChangeDetectorRef,
		protected containerLayerManager: ContainerLayerManager
	) {
		this.windowResize();
		this.windowResizeHandler = () => this.windowResize();
		Hammer.on(window, 'resize', this.windowResizeHandler);

		this.minWidth = this.minWidth || this.width;
		this.minHeight = this.minHeight || this.height;
		this.deadZoneSize = this.deadZoneSize || 0;
		this.zIndex = this.zIndex || 10001; // designated z-index, default to 10001, this is what multi select menu & undo use
		this.currentZIndex = this.zIndex;
	}

	public ngOnInit() {}

	public ngAfterViewInit() {
		this.isInitialized = true;
		this.containerLayerManager.register(this, this.containerType, this.zIndex);
	}

	public ngOnDestroy() {
		this.changeDetectorRef.detach();
		this.destroy();
	}

	@HostListener('mousedown', ['$event'])
	public onMouseDown() {
		this.bringToTop();
	}

	public getWidth(): number {
		return this.width;
	}

	public getHeight(): number {
		return this.height;
	}

	public setWidth(width: number): void {
		this.width = width;
		this.changeDetectorRef.detectChanges();
	}

	public setHeight(height: number): void {
		this.height = height;
		this.changeDetectorRef.detectChanges();
	}

	public getX(): number {
		return this.x;
	}

	public setX(x: number): void {
		this.x = x;
		this.updatePosition();
	}

	public getY(): number {
		return this.y;
	}

	public setY(y: number): void {
		this.y = y;
		this.updatePosition();
	}

	public setZIndex(zIndex: number): void {
		this.currentZIndex = zIndex;
		this.changeDetectorRef.detectChanges();
	}

	public getContainerType(): ContainerType {
		return this.containerType;
	}

	public snapToTop(): void {
		this.y = 0;
	}

	public snapToBottom(): void {
		this.y = this.viewportHeight - this.container.nativeElement.offsetHeight;
	}

	public snapToLeftEdge(): void {
		this.x = 0;
		this.updatePosition();
	}

	public snapToRightEdge(): void {
		this.x = this.viewportWidth - this.container.nativeElement.offsetWidth;
		this.updatePosition();
	}

	public centerHorizontally(left: number, right: number): void {
		this.x = (left + right - this.container.nativeElement.offsetWidth) / 2;
		this.updatePosition();
	}

	public centerVertically(top: number, bottom: number): void {
		this.y = (top + bottom - this.container.nativeElement.offsetHeight) / 2;
		this.updatePosition();
	}

	public bringToTop(): void {
		this.containerLayerManager.bringToTop(this.containerType, this.zIndex);
	}

	protected updatePosition(x: number = this.x, y: number = this.y) {
		if (this.isInitialized) {
			this.x = Math.min(
				Math.max(this.deadZoneSize, x),
				this.viewportWidth - this.container.nativeElement.offsetWidth - this.deadZoneSize
			);
			this.y = Math.min(
				Math.max(this.deadZoneSize, y),
				this.viewportHeight - this.container.nativeElement.offsetHeight - this.deadZoneSize
			);
			this.changeDetectorRef.detectChanges();
		}
	}

	protected windowResize(): void {
		this.viewportWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
		this.viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
		this.width = this.width > this.viewportWidth ? this.viewportWidth : this.width;
		this.height = this.height > this.viewportHeight ? this.viewportHeight : this.height;
		this.updatePosition();
	}

	protected destroy(): void {
		this.containerLayerManager.unregister(this.containerType, this.zIndex);
		this.shutdown$.next();
		this.shutdown$.complete();
		Hammer.off(window, 'resize', this.windowResizeHandler);
	}
}
